import { useState } from 'react';
import type { FinancialYearGroup } from '../financialYearGrouping';
import { formatInr } from '@/lib/format';
import { MonthRow } from './MonthRow';
import { ChevronIcon } from '@/components/icons';

interface FinancialYearSectionProps {
  group: FinancialYearGroup;
  /** Whether this section should start expanded (used for the current/most-recent FY). */
  defaultExpanded?: boolean;
}

export function FinancialYearSection({ group, defaultExpanded = false }: FinancialYearSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="card">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'var(--color-text)',
        }}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>FY {group.financialYear}</h3>
          <ChevronIcon size={18} className={`disclosure-chevron${expanded ? ' is-expanded' : ''}`} />
        </div>
        <div className="stack" style={{ gap: 9, marginTop: 12 }}>
          <SummaryRow label="Opening Balance" value={formatInr(group.openingBalance, true)} />
          <SummaryRow label="EMI Paid" value={formatInr(group.totalEmiPaid, true)} />
          <SummaryRow label="Interest Paid" value={formatInr(group.totalInterest, true)} />
          <SummaryRow label="Principal Repaid (EMI)" value={formatInr(group.totalPrincipalViaEmi, true)} />
          {group.totalPrepayment > 0 && (
            <SummaryRow label="Prepayment" value={formatInr(group.totalPrepayment, true)} />
          )}
          <SummaryRow label="Total Principal Reduction" value={formatInr(group.totalPrincipalReduction, true)} />
          <SummaryRow label="Total Payment" value={formatInr(group.totalPayment, true)} />
          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 2, paddingTop: 9 }}>
            <SummaryRow label="Closing Balance" value={formatInr(group.closingBalance, true)} emphasize="subtle" />
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{ marginTop: 16 }}>
          {group.months.map((month) => (
            <MonthRow key={month.emiDate} month={month} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, emphasize }: { label: string; value: string; emphasize?: 'subtle' }) {
  const emphasisClass = emphasize === 'subtle' ? ' figure-emphasis' : '';
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span className="text-muted">{label}</span>
      <span className={`mono${emphasisClass}`}>{value}</span>
    </div>
  );
}
